import { useState, type FC } from 'react';
import type { PartnerDto } from '@/api';
import {
  Button,
  Combobox,
  DebouncedSearchInput,
  Select,
  useDebouncedValue,
} from '@/ui';

import { usePartner, usePartnerOptions, useProductGroups } from './api/queries';
import type {
  ResolvedSampleTestsSearch,
  SampleTestsSearch,
  StatusFilter,
} from './lib/searchParams';
import { SAMPLE_TEST_STATUSES } from './lib/status';

export interface SampleTestsFiltersProps {
  resolved: ResolvedSampleTestsSearch;
  /** Есть что сбрасывать - кнопка «Сбросить». */
  hasFilters: boolean;
  onChange: (patch: Partial<SampleTestsSearch>) => void;
  onReset: () => void;
}

/**
 * Панель фильтров плана - .filter-bar макета: поиск, статус, клиент, группа
 * товара, «Пора звонить» и «Сбросить».
 */
export const SampleTestsFilters: FC<SampleTestsFiltersProps> = ({
  resolved,
  hasFilters,
  onChange,
  onReset,
}) => {
  const groups = useProductGroups();

  // В адресе только id клиента: подпись поля - из выбора, а после перезагрузки
  // страницы - запросом клиента по id.
  const [partnerQuery, setPartnerQuery] = useState('');
  const partnerOptions = usePartnerOptions(
    useDebouncedValue(partnerQuery, 300),
  );
  const [picked, setPicked] = useState<PartnerDto | null>(null);
  const loadedPartner = usePartner(
    picked?.id === resolved.partner ? undefined : resolved.partner,
  );
  const selectedPartner =
    resolved.partner === undefined
      ? null
      : picked?.id === resolved.partner
        ? picked
        : (loadedPartner.data ?? null);

  // Группа из адреса может не попасть в список - её всё равно показываем.
  const groupNames = groups.data ?? [];
  const groupOptions =
    resolved.group !== '' && !groupNames.includes(resolved.group)
      ? [resolved.group, ...groupNames]
      : groupNames;

  return (
    <div className="mb-3.5 flex flex-wrap items-center gap-2 rounded-lg border border-ink-200 bg-white px-3.5 py-3">
      <DebouncedSearchInput
        className="min-w-[220px] flex-1"
        placeholder="Клиент, товар, № отгрузки..."
        aria-label="Поиск по клиенту, товару или номеру отгрузки"
        value={resolved.search}
        onCommit={(value) => onChange({ search: value || undefined })}
      />
      <Select
        aria-label="Статус теста"
        value={resolved.status}
        onChange={(event) => {
          const status = event.target.value as StatusFilter;
          onChange({ status: status === 'Testing' ? undefined : status });
        }}
      >
        <option value="Testing">{SAMPLE_TEST_STATUSES.Testing.label}</option>
        <option value="Succeeded">
          {SAMPLE_TEST_STATUSES.Succeeded.label}
        </option>
        <option value="Failed">{SAMPLE_TEST_STATUSES.Failed.label}</option>
        <option value="closed">Закрытые</option>
        <option value="all">Все статусы</option>
      </Select>
      <Combobox
        className="w-[220px]"
        aria-label="Клиент"
        value={selectedPartner}
        onChange={(partner) => {
          setPicked(partner);
          onChange({ partner: partner?.id });
        }}
        onQueryChange={setPartnerQuery}
        options={partnerOptions.data?.items ?? []}
        isLoading={partnerOptions.isFetching}
        getKey={(partner) => partner.id}
        getLabel={(partner) => partner.name}
        placeholder="Все клиенты"
        emptyLabel="Клиент не найден"
      />
      <Select
        aria-label="Группа товара"
        value={resolved.group}
        onChange={(event) =>
          onChange({ group: event.target.value || undefined })
        }
      >
        <option value="">Все группы</option>
        {groupOptions.map((name) => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
      </Select>
      <label className="inline-flex items-center gap-1.5 px-1.5 text-[13px] text-ink-700">
        <input
          type="checkbox"
          checked={resolved.due}
          onChange={(event) =>
            onChange({ due: event.target.checked || undefined })
          }
        />
        Пора звонить
      </label>
      {hasFilters && (
        <Button size="sm" onClick={onReset}>
          Сбросить
        </Button>
      )}
    </div>
  );
};
